import { supabase } from "@/lib/supabase";
import { useEffect, useState } from "react";
import { parseISO } from "date-fns";

export function useReportDataByYear(year?: number) {
  const [reportByCurrency, setReportByCurrency] = useState({});
  const [categoryReportByCurrency, setCategoryReportByCurrency] = useState({});

  useEffect(() => {
    if (!year) return;

    const fetch = async () => {
      const start = `${year}-01-01`;
      const end = `${year}-12-31`;

      const { data: txs }: any = await supabase
        .from("transactions")
        .select(
          "amount, type, date, category_id, account:accounts!inner(status, name, currency),categories( * ) "
        )
        .eq("account.status", true)
        .gte("date", start)
        .lte("date", end);

      const grouped: Record<
        string, // currency
        Record<
          string, // month
          {
            income: number;
            expense: number;
            balance: number;
            accounts: Record<
              string,
              { income: number; expense: number; balance: number }
            >;
          }
        >
      > = {};

      const byCategory: Record<
        string, // currency
        {
          income: Record<string, number>;
          expense: Record<string, number>;
        }
      > = {};

      for (const tx of txs) {
        const date = parseISO(tx.date);
        const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}`;
        const currency = tx.account?.currency || "Sin moneda";
        const accountName = tx.account?.name || "Sin cuenta";
        const catKey = tx.categories?.name
          ? tx.categories?.icon + " " + tx.categories?.name
          : "Sin categoría";
        // const catKey = tx.category_id || "Sin categoría";

        // --- Agrupación mensual por moneda ---
        if (!grouped[currency]) grouped[currency] = {};
        if (!grouped[currency][monthKey]) {
          grouped[currency][monthKey] = {
            income: 0,
            expense: 0,
            balance: 0,
            accounts: {},
          };
        }

        const monthData = grouped[currency][monthKey];
        monthData[tx.type] += tx.amount;
        monthData.balance = monthData.income - monthData.expense;

        if (!monthData.accounts[accountName]) {
          monthData.accounts[accountName] = {
            income: 0,
            expense: 0,
            balance: 0,
          };
        }

        monthData.accounts[accountName][tx.type] += tx.amount;
        monthData.accounts[accountName].balance =
          monthData.accounts[accountName].income -
          monthData.accounts[accountName].expense;

        // --- Agrupación por categoría ---
        if (!byCategory[currency]) {
          byCategory[currency] = {
            income: {},
            expense: {},
          };
        }

        byCategory[currency][tx.type][catKey] =
          (byCategory[currency][tx.type][catKey] || 0) + tx.amount;
      }

      // Estructura final de categorías
      const formattedCategoryReport = Object.entries(byCategory).reduce(
        (acc, [currency, data]) => {
          acc[currency] = {
            income: Object.entries(data.income).map(([cat, amount]) => ({
              category: cat,
              amount,
            })),
            expense: Object.entries(data.expense).map(([cat, amount]) => ({
              category: cat,
              amount,
            })),
          };
          return acc;
        },
        {} as Record<
          string,
          {
            income: { category: string; amount: number }[];
            expense: { category: string; amount: number }[];
          }
        >
      );

      setReportByCurrency(grouped);
      setCategoryReportByCurrency(formattedCategoryReport);
    };

    fetch();
  }, [year]);

  return { reportByCurrency, categoryReportByCurrency };
}
